"use client";

import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { BellRing, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import Image from "next/image";

// Fake data for the live feed
const initialAlerts = [
  { 
    artist: "Lil Uzi Vert", 
    tracks: 1, 
    image: "https://thefader-res.cloudinary.com/private_images/w_1440,c_limit,f_auto,q_auto:best/Fader_105_Lil_Uzi_RachelChandler_WEB_201606300002_yoo383/lil-uzi-vert-interview-the-perfect-luv.jpg"
  },
  { 
    artist: "Osamason", 
    tracks: 7, 
    image: "https://acrn.com/wp-content/uploads/2024/10/picture1.png?w=700"
  },
  { 
    artist: "Juice WRLD", 
    tracks: 14, 
    image: "https://i.iheart.com/v3/catalog/artist/32086512?ops=fit(720%2C720)"
  }
];

const newAlert = {
  artist: "Playboi Carti",
  tracks: 1,
  type: "Snippet",
  image: "https://i.guim.co.uk/img/media/a6d708b3363d3537ce431767e9c8b1cc58927eeb/1063_5_2768_1661/master/2768.jpg?width=1200&height=1200&quality=85&auto=format&fit=crop&s=1def6c38e0b6db9a68ba5a6e2dbc9735"
};

export function HeroSection() {
  const router = useRouter();
  const [alerts, setAlerts] = useState(initialAlerts);
  const [showNewAlert, setShowNewAlert] = useState(false);
  const [isWaitlistEnabled, setIsWaitlistEnabled] = useState(true);

  useEffect(() => {
    // Check if waitlist is enabled from environment variable
    const checkWaitlist = async () => {
      try {
        const response = await fetch('/api/waitlist/status');
        const data = await response.json();
        setIsWaitlistEnabled(data.enabled);
      } catch (error) {
        console.error('Error checking waitlist status:', error);
      }
    };
    
    checkWaitlist();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNewAlert(true);
      setAlerts([{ artist: newAlert.artist, tracks: newAlert.tracks, image: newAlert.image }, ...alerts.slice(0, 2)]);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 mt-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-16">
          {/* Hero Text Content */}
          <div className="w-full lg:max-w-[600px] lg:pt-8 text-center lg:text-left">
            <div className="mb-8 inline-flex justify-center lg:justify-start w-full lg:w-auto">
              <div className="relative rounded-full px-4 py-2 text-sm leading-6 text-gray-300 ring-1 ring-purple-500/30 hover:ring-purple-500/50 bg-gray-900/50 flex items-center gap-2">
                <span className="flex items-center">
                  <span className="text-purple-400 font-semibold">$5 OFF</span>
                  <span className="mx-1">•</span>
                  First 100 Premium Subscribers
                </span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-purple-400">
              Get Alerted{' '}
              <span className="inline-block">Instantly{' '}When</span>{' '}
              <br className="hidden sm:block" />
              <span className="inline-block">Songs Leak</span>
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-purple-300 font-medium">
              Get instant alerts when unreleased tracks surface—before they're gone.
            </p>
            <p className="mt-6 text-base sm:text-lg leading-8 text-gray-300">
              Join thousands of music enthusiasts who get real-time notifications for unreleased tracks, 
              snippets, and demos from their favorite artists—direct to their phone or inbox.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-x-6">
              <Button
                onClick={() => router.push(isWaitlistEnabled ? '/waitlist' : '/sign-in')}
                className="w-full sm:w-auto bg-purple-600 hover:bg-purple-500 text-base sm:text-lg py-6 px-8 group relative overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10 flex items-center justify-center">
                  {isWaitlistEnabled ? "Join Waitlist" : "Start Free"}
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const element = document.getElementById('how-it-works');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto text-base sm:text-lg py-6 px-8 border-purple-700 hover:bg-purple-950/50"
              >
                See How It Works
              </Button>
            </div>
            <div className="mt-8 sm:mt-10 flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 text-sm text-gray-400">
              <div className="flex items-center">
                <BellRing className="h-4 w-4 mr-2 text-purple-500" />
                Real-time alerts
              </div>
              <div className="flex items-center">
                <svg className="h-4 w-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                End-to-end encrypted
              </div>
              <div className="flex items-center">
                <svg className="h-4 w-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                100% automated
              </div>
            </div>
          </div>

          {/* Live Feed Component */}
          <div className="w-full sm:w-[450px] bg-[#0A0A0A]/90 backdrop-blur-xl rounded-2xl p-4 sm:p-6 border border-gray-800/60 shadow-2xl mt-8 lg:mt-0">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h3 className="text-lg sm:text-xl font-semibold text-white">New Alerts</h3>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-400">Live</span>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {alerts.map((alert, index) => (
                <div
                  key={alert.artist + index}
                  className={`group flex items-center justify-between p-3 sm:p-4 rounded-xl bg-[#111111] hover:bg-[#161616] border border-gray-800/50 transition-all duration-300 ${
                    index === 0 && showNewAlert ? 'animate-slide-in-top ring-1 ring-purple-500/50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="relative w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden ring-2 ring-purple-500/10 transition-all duration-300 group-hover:scale-105 group-hover:ring-purple-500/20">
                      <Image
                        src={alert.image}
                        alt={alert.artist}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm sm:text-[15px] font-medium text-white group-hover:text-purple-200 transition-colors duration-300">
                        {alert.artist}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {alert.tracks} {index === 0 && alert.artist === newAlert.artist ? newAlert.type : (alert.tracks === 1 ? 'Track' : 'Tracks')}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-purple-400/90 bg-purple-500/10 px-2 sm:px-2.5 py-1 rounded-full">
                    New
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add this to your globals.css or create a new style block
const styles = `
@keyframes slide-in-top {
  0% {
    transform: translateY(-20px);
    opacity: 0;
    background-color: #111111;
  }
  20% {
    background-color: rgba(168, 85, 247, 0.15);
  }
  80% {
    background-color: rgba(168, 85, 247, 0.15);
  }
  100% {
    transform: translateY(0);
    opacity: 1;
    background-color: #111111;
  }
}

.animate-slide-in-top {
  animation: slide-in-top 1.2s ease-out forwards;
}
`;